import { gql } from "@apollo/client";

import { SITUATION_FIELDS_FOR_GROWNUP } from "@/graphql/queries/situations";

/** Saving is a merge, so a half-filled sheet keeps what was already there. */
export const SUBMIT_SITUATION_ANSWERS = gql`
  mutation SubmitSituationAnswers(
    $studentId: ID!
    $situationKey: String!
    $answers: [SituationAnswerInput!]!
  ) {
    submitSituationAnswers(
      studentId: $studentId
      situationKey: $situationKey
      answers: $answers
    ) {
      errors
      situation {
        id
        key
        questionCount
        answeredCount
        submittedAt
        questions {
          id
          key
          answer
          reasonAnswer
          counterAnswer
        }
      }
    }
  }
`;

/**
 * Assembles a set for one learner: unanswered situations, spread across problem
 * areas. No AI call and no credits — this only chooses which problems.
 *
 * `shortfallReason` comes back when it holds fewer than asked for. A set quietly
 * shorter than requested reads as a broken generator, which is the one diagnosis
 * that leads nowhere.
 */
export const CREATE_SITUATION_SESSION = gql`
  mutation CreateSituationSession($studentId: ID!, $count: Int) {
    createSituationSession(studentId: $studentId, count: $count) {
      errors
      shortfallReason
      session {
        id
        size
        doneCount
        done
        nextKey
        personalized
        createdAt
        situations {
          id
          key
          title
          themeLabels
          answeredCount
          questionCount
        }
      }
    }
  }
`;

/** Throws away the assignment. Every answer the learner wrote in it survives. */
export const DELETE_SITUATION_SESSION = gql`
  mutation DeleteSituationSession($sessionId: ID!) {
    deleteSituationSession(sessionId: $sessionId) {
      errors
    }
  }
`;

/**
 * Rewords a whole session around the learner's world — one AI call per situation,
 * run together.
 *
 * `warnings` names the situations that kept the file's wording. Not errors: the
 * session is solvable either way, and „nepavyko" for a set of three says nothing
 * about which one a grown-up is now looking at unrewritten.
 */
export const PERSONALIZE_SITUATION_SESSION = gql`
  ${SITUATION_FIELDS_FOR_GROWNUP}
  mutation PersonalizeSituationSession($sessionId: ID!, $profile: String!) {
    personalizeSituationSession(sessionId: $sessionId, profile: $profile) {
      errors
      warnings
      session {
        id
        personalized
        personalizationTheme
        situations {
          ...SituationFieldsForGrownUp
        }
      }
    }
  }
`;

/**
 * A grown-up's own wording for one situation of a session.
 *
 * Structure is refused for anybody; drift is only a warning — a person who put
 * the learner's name in the story read it and made a choice, and the app's job is
 * to say why it matters rather than refuse.
 */
export const SAVE_SITUATION_SESSION_PERSONALIZATION = gql`
  ${SITUATION_FIELDS_FOR_GROWNUP}
  mutation SaveSituationSessionPersonalization(
    $sessionId: ID!
    $situationKey: String!
    $story: String!
    $intro: String
    $questions: [SituationPromptInput!]!
  ) {
    saveSituationSessionPersonalization(
      sessionId: $sessionId
      situationKey: $situationKey
      story: $story
      intro: $intro
      questions: $questions
    ) {
      errors
      warnings
      session {
        id
        personalized
        personalizationTheme
        situations {
          ...SituationFieldsForGrownUp
        }
      }
    }
  }
`;

/**
 * Back to the wording in the files — one situation, or the whole session when
 * `situationKey` is omitted. The learner's answers are untouched: they hang off
 * the problem, never off the wording.
 */
export const RESET_SITUATION_SESSION_PERSONALIZATION = gql`
  ${SITUATION_FIELDS_FOR_GROWNUP}
  mutation ResetSituationSessionPersonalization($sessionId: ID!, $situationKey: String) {
    resetSituationSessionPersonalization(sessionId: $sessionId, situationKey: $situationKey) {
      errors
      session {
        id
        personalized
        personalizationTheme
        situations {
          ...SituationFieldsForGrownUp
        }
      }
    }
  }
`;

/**
 * A grown-up's note on one answer. Omitting `mark` clears it, which is the
 * ordinary state and means accepted — not "unreviewed".
 */
export const MARK_SITUATION_ANSWER = gql`
  mutation MarkSituationAnswer(
    $studentId: ID!
    $situationKey: String!
    $questionKey: String!
    $mark: String
  ) {
    markSituationAnswer(
      studentId: $studentId
      situationKey: $situationKey
      questionKey: $questionKey
      mark: $mark
    ) {
      errors
      situation {
        id
        key
        needsPracticeCount
        reviewedAt
        questions {
          id
          key
          mark
        }
      }
    }
  }
`;
